/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { formatBytes } from "@/utils/formatBytes";
import { formatters } from "@/utils/timeSettings";

export interface TrafficPoint {
  time: string;
  in: number;
  out: number;
}

interface SNMPTrafficChartProps {
  data: TrafficPoint[];
  height?: number;
}

const formatAxisTick = (value: number) => {
  if (value === 0) return "0";
  const sizes = ["B", "KiB", "MiB", "GiB", "TiB"];
  const k = 1024;
  const i = Math.floor(Math.log(value) / Math.log(k));
  return `${Math.round(value / Math.pow(k, i))}${sizes[i]}`;
};

const formatXAxisTick = (tickItem: string) => formatters.time(new Date(tickItem));

export const SNMPTrafficChart: React.FC<SNMPTrafficChartProps> = ({
  data,
  height = 120,
}) => {
  if (!data || data.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-xs text-gray-400 dark:text-gray-500"
        style={{ height }}
      >
        No traffic history yet
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="snmpInGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#86b9a0" stopOpacity={0.15} />
            <stop offset="95%" stopColor="#86b9a0" stopOpacity={0} />
          </linearGradient>
          <linearGradient id="snmpOutGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#9aa39c" stopOpacity={0.15} />
            <stop offset="95%" stopColor="#9aa39c" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" opacity={0.15} vertical={false} />
        <XAxis
          dataKey="time"
          tickFormatter={formatXAxisTick}
          tick={{ fontSize: 10 }}
          minTickGap={40}
        />
        <YAxis tickFormatter={formatAxisTick} tick={{ fontSize: 10 }} width={44} />
        <Tooltip
          formatter={(value: number) => formatBytes(value)}
          labelFormatter={(label) => formatters.time(new Date(label))}
        />
        <Area
          type="monotone"
          dataKey="in"
          stroke="#86b9a0"
          strokeWidth={2}
          fill="url(#snmpInGradient)"
          name="In"
        />
        <Area
          type="monotone"
          dataKey="out"
          stroke="#9aa39c"
          strokeWidth={2}
          fill="url(#snmpOutGradient)"
          name="Out"
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};
